export default defineNuxtPlugin((nuxtApp) => {
  if (typeof window === 'undefined') return;

  const scriptId = 'emarsys-webpush-sdk';
  let sdkInitialized = false;

  // Inject Nuxt-compatible helper immediately
  nuxtApp.provide('emarsysPush', (action: string, options?: any) => {
    const sdk = (window as any).WebEmarsysSdk;
    if (sdk && typeof sdk[action] === 'function') {
      sdk[action](options);
    } else {
      console.error(`[Emarsys Web Push] SDK not available to execute action: ${action}`);
    }
  });

  // Delay all initialization, diagnostics, and watchers until app:mounted
  nuxtApp.hook('app:mounted', () => {
    const floristStore = useFloristInfoStore();

    // Unified, robust, and sequential function to perform subscription and binding
    const sequentialBinding = async () => {
      const sdk = (window as any).WebEmarsysSdk;
      const config = useRuntimeConfig();
      const shopFieldId = Number(config.public.emarsysShopFieldId) || 14261;

      const floristId = floristStore.floristInfo?.floristId;
      const countryCode =
        floristStore.floristInfo?.unitCountryCode || floristStore.floristInfo?.shopLocation?.countryCode;

      if (!floristId) {
        console.error('[Emarsys Web Push] No active florist session. Waiting for login to bind...');
        return;
      }

      if (!countryCode) {
        console.error(
          `[Emarsys Web Push] Florist ${floristId} does not have a valid countryCode (unitCountryCode or shopLocation.countryCode). Aborting push binding to avoid incorrect multi-country routing.`
        );
        return;
      }

      // Multi-country unique shop key to bind the device in Emarsys
      const uniqueFloristKey = `${countryCode.trim().toUpperCase()}${floristId}`;

      if (!sdk) {
        console.error('[Emarsys Web Push] SDK not available yet for binding. Waiting for it to load.');
        return;
      }

      // Step 1: Establish known contact identity in Emarsys SDK before registering push token
      if (typeof sdk.login === 'function') {
        try {
          const loginRes = sdk.login({
            fieldId: shopFieldId,
            fieldValue: uniqueFloristKey,
          });
          if (loginRes && typeof loginRes.then === 'function') {
            await loginRes;
          }
        } catch (loginErr) {
          console.error('[Emarsys Web Push] Warning during pre-subscribe login:', loginErr);
        }
      }

      // Step 2: Register device push token under the established contact
      if (typeof sdk.subscribe === 'function') {
        try {
          const res = sdk.subscribe();
          if (res && typeof res.then === 'function') {
            await res;
          }
          // Step 3: Re-affirm binding after subscription to guarantee device link in Emarsys
          if (typeof sdk.login === 'function') {
            const postLoginRes = sdk.login({
              fieldId: shopFieldId,
              fieldValue: uniqueFloristKey,
            });
            if (postLoginRes && typeof postLoginRes.then === 'function') {
              await postLoginRes;
            }
          }
        } catch (subErr) {
          console.error('[Emarsys Web Push] Error during device push subscription:', subErr);
        }
      }
    };

    // Initialize the SDK with fail-safe error handling
    const initEmarsys = () => {
      try {
        const sdk = (window as any).WebEmarsysSdk;
        const config = useRuntimeConfig();
        const appCode = config.public.emarsysApplicationCode || 'EMS9E-FAB1C';
        const vapidKey =
          config.public.emarsysVapidPublicKey ||
          'BNZQq8dCwVuOqpf5iVZ0p4jRoH3CR72cL8QljDH_1TpMM3502uoqt90IlYReLTnRr76-H-AfCtCY3cSDyP2UTOc';

        if (sdk && typeof sdk.init === 'function') {
          sdk.init({
            applicationCode: appCode,
            autoSubscribe: false,
            serviceWorker: {
              url: 'emarsys-sw.js',
              applicationServerPublicKey: vapidKey,
            },
          });

          sdkInitialized = true;
          // Give 1 second for the SDK to complete its internal asynchronous initialization
          setTimeout(() => {
            sequentialBinding().catch((err) => {
              console.error('[Emarsys Web Push] Non-blocking error in sequentialBinding execution:', err);
            });
          }, 1000);
        } else {
          console.error('[Emarsys Web Push] WebEmarsysSdk object not ready after script load.');
        }
      } catch (initErr) {
        console.error('[Emarsys Web Push] Non-blocking exception during SDK initialization:', initErr);
      }
    };

    // Controlled asynchronous loading via script onload event
    let script = document.getElementById(scriptId) as HTMLScriptElement;
    if (!script) {
      script = document.createElement('script');
      script.id = scriptId;
      script.async = true;
      script.src = 'https://client-version.cf.emarsys.net/web-emarsys-sdk-v4/latest/web-emarsys-sdk.js';
      script.onload = () => {
        initEmarsys();
      };
      script.onerror = (err) => {
        console.error('[Emarsys Web Push] Non-blocking error downloading Emarsys SDK script.', err);
      };
      document.head.appendChild(script);
    } else {
      const checkInterval = setInterval(() => {
        if ((window as any).WebEmarsysSdk) {
          clearInterval(checkInterval);
          initEmarsys();
        }
      }, 50);
      setTimeout(() => clearInterval(checkInterval), 5000);
    }

    // Unique and sequential watcher to re-bind the florist if their state changes
    watch(
      () => floristStore.floristInfo?.floristId,
      () => {
        try {
          if (sdkInitialized) {
            sequentialBinding().catch((err) => {
              console.error('[Emarsys Web Push] Non-blocking error in watcher sequentialBinding:', err);
            });
          }
        } catch (watchErr) {
          console.error('[Emarsys Web Push] Non-blocking error in floristId watcher:', watchErr);
        }
      }
    );
  });
});
